import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Netlify Forms submission - encode form data properly
      const encode = (data) => {
        return Object.keys(data)
          .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
          .join("&");
      };

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'inquiry',
          'name': formData.name,
          'email': formData.email,
          'phone': formData.phone,
          'company': formData.company,
          'inquiry_type': formData.inquiry_type,
          'message': formData.message,
        }),
      });
      
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiry_type: 'general',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 px-6 md:px-12 bg-navy relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-30" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 rounded-full mb-6">
            <Mail size={20} className="text-gold" />
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">Contact Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Let's <span className="text-gold-gradient">Connect</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Get in touch with us for inquiries about our export products or financial services.
          </p>
          <div className="section-divider mt-8" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="glass-panel p-8 md:p-12 rounded-lg">
            <h3 className="text-2xl font-heading font-semibold text-white mb-8">Send us a message</h3>
            
            {/* Hidden form for Netlify detection */}
            <form name="inquiry" netlify netlify-honeypot="bot-field" hidden>
              <input type="hidden" name="form-name" value="inquiry" />
              <input type="text" name="name" />
              <input type="email" name="email" />
              <input type="tel" name="phone" />
              <input type="text" name="company" />
              <select name="inquiry_type">
                <option value="general">General Inquiry</option>
                <option value="export">Export Products</option>
                <option value="financial">Financial Services</option>
                <option value="consultancy">Export Consultancy</option>
              </select>
              <textarea name="message"></textarea>
            </form>

            {/* Actual React form */}
            <form onSubmit={handleSubmit} name="inquiry" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" className="space-y-6" data-testid="contact-form">
              <input type="hidden" name="form-name" value="inquiry" />
              
              {/* Honeypot field for spam protection */}
              <div style={{ display: 'none' }}>
                <label>
                  Don't fill this out if you're human: <input name="bot-field" />
                </label>
              </div>
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-silver mb-2 font-medium">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={1}
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white placeholder:text-gray-500 px-4 py-3 outline-none transition-colors"
                  placeholder="John Doe"
                  data-testid="contact-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-silver mb-2 font-medium">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white placeholder:text-gray-500 px-4 py-3 outline-none transition-colors"
                  placeholder="john@example.com"
                  data-testid="contact-email-input"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-silver mb-2 font-medium">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  minLength={10}
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white placeholder:text-gray-500 px-4 py-3 outline-none transition-colors"
                  placeholder="+91 XXXXXXXXXX"
                  data-testid="contact-phone-input"
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-silver mb-2 font-medium">
                  Company Name (Optional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white placeholder:text-gray-500 px-4 py-3 outline-none transition-colors"
                  placeholder="Your Company"
                  data-testid="contact-company-input"
                />
              </div>

              {/* Inquiry Type */}
              <div>
                <label htmlFor="inquiry_type" className="block text-silver mb-2 font-medium">
                  Inquiry Type *
                </label>
                <select
                  id="inquiry_type"
                  name="inquiry_type"
                  value={formData.inquiry_type}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white px-4 py-3 outline-none transition-colors"
                  data-testid="contact-inquiry-type-select"
                >
                  <option value="general" className="bg-navy">General Inquiry</option>
                  <option value="export" className="bg-navy">Export Products</option>
                  <option value="financial" className="bg-navy">Financial Services</option>
                  <option value="consultancy" className="bg-navy">Export Consultancy</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-silver mb-2 font-medium">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  minLength={10}
                  rows={5}
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white placeholder:text-gray-500 px-4 py-3 outline-none transition-colors resize-none"
                  placeholder="Tell us about your requirements..."
                  data-testid="contact-message-textarea"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-gold w-full flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Inquiry
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details Card */}
            <div className="glass-panel p-8 rounded-lg">
              <h3 className="text-2xl font-heading font-semibold text-white mb-6">Get in Touch</h3>
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start gap-4" data-testid="contact-email-info">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Mail size={24} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-silver font-semibold mb-1">Email</div>
                    <a
                      href="mailto:admin@agatsyainternational.com"
                      className="text-gray-300 hover:text-gold transition-colors"
                    >
                      admin@agatsyainternational.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4" data-testid="contact-phone-info">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Phone size={24} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-silver font-semibold mb-1">Phone</div>
                    <a
                      href="tel:+919225059195"
                      className="text-gray-300 hover:text-gold transition-colors"
                    >
                      +91 9225059195
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4" data-testid="contact-address-info">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <MapPin size={24} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-silver font-semibold mb-1">Address</div>
                    <p className="text-gray-300">
                      16/1185, Behind Annaram Gonda School,<br />
                      Ichalkaranji, Kolhapur,<br />
                      Maharashtra, India - 416115
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="glass-panel p-8 rounded-lg">
              <h3 className="text-xl font-heading font-semibold text-white mb-4">Business Hours</h3>
              <div className="space-y-2 text-gray-300">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span className="text-gold">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="text-gold">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="text-gold">9:00 AM - 1:00 PM</span>
                </div>
              </div>
            </div>

            {/* Quick Response Badge */}
            <div className="glass-panel p-6 rounded-lg bg-gradient-to-r from-gold/10 to-silver/10 border-gold-glow">
              <p className="text-center text-white font-semibold">
                <span className="text-gold-gradient">Quick Response Guaranteed</span>
                <br />
                <span className="text-sm text-gray-300 mt-2 block">We typically respond within 24 hours</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
